import { useRef, useState } from 'react';

/**
 * Die Diagramme des Statistik-Reiters.
 *
 * OHNE BIBLIOTHEK, wie `Verlaufsgraph`. Das Bündel wird beim Bauen schon als zu
 * groß gemeldet; eine Diagrammbibliothek für zwei Formen wäre die falsche
 * Antwort darauf.
 *
 * ══ WELCHE FORM WOFÜR ══════════════════════════════════════════════════════
 *
 * **Säulen für Tageswerte.** Ein Tag ist eine gezählte Menge, kein Messpunkt
 * auf einer stetigen Kurve. Eine Linie zwischen Montag und Dienstag behauptet
 * Zwischenwerte, die es nicht gibt. Erst wenn so viele Tage nebeneinander
 * stehen, dass die Säulen zu Strichen werden (ab etwa fünf Wochen), ist die
 * Linie die ehrlichere Darstellung — dann geht es ohnehin um den Verlauf und
 * nicht mehr um den einzelnen Tag.
 *
 * **Zwei getrennte Diagramme für Aufrufe und Besuche.** Nie zwei Achsen in
 * einem Bild: die Größenordnungen unterscheiden sich um das Drei- bis
 * Fünffache, und zwei übereinandergelegte Skalen erfinden einen Zusammenhang,
 * den die Daten nicht hergeben.
 *
 * **Waagerechte Balken für Ranglisten.** Verweiser, Länder und Browser tragen
 * lange Namen; senkrechte Säulen zwängen die Beschriftung in die Schräge.
 *
 * ══ EINE FARBE, KEIN FARBVERLAUF ═══════════════════════════════════════════
 *
 * Alle Balken einer Rangliste haben dieselbe Farbe. Sie dunkler zu färben, wo
 * der Wert größer ist, verdoppelt nur, was die Länge schon sagt — und verbraucht
 * den einzigen freien Kanal für nichts.
 *
 * Die Farbe kommt über `currentColor` aus den Token, kippt also in der dunklen
 * Fassung mit. Geprüft: Kontrast zur Fläche über 3:1 in beiden Fassungen.
 */

const RAND = { oben: 10, unten: 20, links: 38, rechts: 8 };
const HOEHE = 140;
/** Säulen nie den ganzen Platz füllen lassen — der Rest ist Luft, keine Verschwendung. */
const MAX_SAEULE = 24;
/** Ab hier werden die Säulen zu Strichen; dann ist die Linie ehrlicher. */
const LINIE_AB_TAGEN = 35;

function abgerundetOben(x: number, y: number, breite: number, hoehe: number, r: number): string {
  const radius = Math.min(r, breite / 2, hoehe);
  return [
    `M${x},${y + hoehe}`,
    `L${x},${y + radius}`,
    `Q${x},${y} ${x + radius},${y}`,
    `L${x + breite - radius},${y}`,
    `Q${x + breite},${y} ${x + breite},${y + radius}`,
    `L${x + breite},${y + hoehe}`,
    'Z',
  ].join(' ');
}

/** Tag als `5.9.` — die Achse braucht kein Jahr, die Überschrift nennt den Zeitraum. */
function tagKurz(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${Number(d)}.${Number(m)}.`;
}

function tagLang(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long' });
}

export interface VerlaufsPunkt {
  tag: string;
  wert: number;
}

/**
 * Ein Verlauf über Tage — eine Reihe, eine Achse.
 *
 * Keine Legende: bei einer Reihe sagt die Überschrift schon, was gezeichnet ist.
 * Ein Kasten mit einem einzigen Farbfeld wiederholte sie nur.
 */
export function Tagesverlauf({
  titel,
  punkte,
  einheit,
}: {
  titel: string;
  punkte: VerlaufsPunkt[];
  /** Wird im Tooltip hinter den Wert gesetzt — „Aufrufe", „Besuche". */
  einheit: string;
}) {
  const [zeiger, setZeiger] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const breite = 560;

  if (punkte.length === 0) return null;

  const maxWert = Math.max(...punkte.map((p) => p.wert), 1);
  const innenB = breite - RAND.links - RAND.rechts;
  const innenH = HOEHE - RAND.oben - RAND.unten;
  const alsLinie = punkte.length > LINIE_AB_TAGEN;

  const bandB = innenB / punkte.length;
  const saeuleB = Math.min(MAX_SAEULE, Math.max(2, bandB - 2)); // 2px Abstand als Trennung
  const xBand = (i: number) => RAND.links + i * bandB;
  const xMitte = (i: number) => xBand(i) + bandB / 2;
  const yFuer = (v: number) => RAND.oben + innenH - (v / maxWert) * innenH;

  // Drei Gitterlinien genügen: null, Mitte, Höchstwert.
  const markenY = [0, Math.round(maxWert / 2), maxWert].filter(
    (v, i, a) => a.indexOf(v) === i,
  );

  // Höchstens sechs Datumsangaben, sonst klebt die Achse zu.
  const schritt = Math.max(1, Math.ceil(punkte.length / 6));

  const linie = alsLinie
    ? punkte.map((p, i) => `${i === 0 ? 'M' : 'L'}${xMitte(i)},${yFuer(p.wert)}`).join(' ')
    : '';
  const flaeche = alsLinie
    ? `${linie} L${xMitte(punkte.length - 1)},${RAND.oben + innenH} L${xMitte(0)},${
        RAND.oben + innenH
      } Z`
    : '';

  const gezeigt = zeiger !== null ? punkte[zeiger] : null;

  function beiBewegung(e: React.MouseEvent<SVGSVGElement>) {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const relativ = ((e.clientX - box.left) / box.width) * breite;
    const i = Math.floor((relativ - RAND.links) / bandB);
    setZeiger(i >= 0 && i < punkte.length ? i : null);
  }

  return (
    <div className="w-full">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-600">{titel}</span>
        <span className="font-mono text-xs tabular-nums text-slate-500">
          {gezeigt ? `${tagLang(gezeigt.tag)}: ${gezeigt.wert} ${einheit}` : ''}
        </span>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${breite} ${HOEHE}`}
        className="w-full text-brand-600"
        style={{ height: HOEHE }}
        onMouseMove={beiBewegung}
        onMouseLeave={() => setZeiger(null)}
        role="img"
        aria-label={titel}
      >
        {/* Gitter: haarfein, durchgezogen, eine Stufe von der Fläche entfernt. */}
        {markenY.map((v) => (
          <g key={v}>
            <line
              x1={RAND.links}
              y1={yFuer(v)}
              x2={breite - RAND.rechts}
              y2={yFuer(v)}
              className="stroke-slate-200"
              strokeWidth={1}
            />
            <text
              x={RAND.links - 6}
              y={yFuer(v) + 3}
              textAnchor="end"
              className="fill-slate-400 text-[9px] tabular-nums"
            >
              {v}
            </text>
          </g>
        ))}

        {alsLinie ? (
          <>
            <path d={flaeche} fill="currentColor" opacity={0.1} />
            <path
              d={linie}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : (
          punkte.map((p, i) => {
            const y = yFuer(p.wert);
            const h = RAND.oben + innenH - y;
            return (
              <path
                key={p.tag}
                d={abgerundetOben(
                  xMitte(i) - saeuleB / 2,
                  p.wert === 0 ? RAND.oben + innenH - 1 : y,
                  saeuleB,
                  p.wert === 0 ? 1 : h,
                  4,
                )}
                fill="currentColor"
                opacity={zeiger === null || zeiger === i ? 1 : 0.45}
              />
            );
          })
        )}

        {/* Der Punkt unter dem Zeiger, mit Ring in Flächenfarbe. */}
        {gezeigt && alsLinie && (
          <circle
            cx={xMitte(zeiger!)}
            cy={yFuer(gezeigt.wert)}
            r={4}
            fill="currentColor"
            className="stroke-surface"
            strokeWidth={2}
          />
        )}

        {punkte.map((p, i) =>
          i % schritt === 0 ? (
            <text
              key={`d-${p.tag}`}
              x={xMitte(i)}
              y={HOEHE - 6}
              textAnchor="middle"
              className="fill-slate-400 text-[9px]"
            >
              {tagKurz(p.tag)}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}

export interface RangZeile {
  name: string;
  wert: number;
  /** Zweiter Wert, nur als Text — etwa Aufrufe neben Besuchen. */
  neben?: number;
}

/**
 * Eine Rangliste als waagerechte Balken.
 *
 * Alle Balken in derselben Farbe: die Länge sagt bereits, was größer ist. Sie
 * zusätzlich einzufärben, hiesse dieselbe Information zweimal zu zeigen und
 * den Farbkanal dafür zu verbrauchen.
 */
export function Rangbalken({
  zeilen,
  wertLabel,
  nebenLabel,
}: {
  zeilen: RangZeile[];
  wertLabel: string;
  nebenLabel?: string;
}) {
  if (zeilen.length === 0) return null;
  const max = Math.max(...zeilen.map((z) => z.wert), 1);

  return (
    <ul className="space-y-1.5">
      {zeilen.map((z) => (
        <li key={z.name} className="group">
          <div className="mb-0.5 flex items-baseline justify-between gap-2 text-xs">
            <span className="truncate text-slate-700" title={z.name}>
              {z.name}
            </span>
            <span className="shrink-0 font-mono tabular-nums text-slate-500">
              {z.wert}
              {z.neben !== undefined && (
                <span className="ml-1.5 text-slate-400">
                  {z.neben} {nebenLabel}
                </span>
              )}
            </span>
          </div>
          {/* Der Balken selbst traegt keine Zahl — sie steht schon darueber.
              Eine Zahl in jedem Balken waere genau die Flut, die dazu fuehrt,
              dass keine mehr gelesen wird. */}
          <div
            className="h-2 w-full overflow-hidden rounded-sm bg-slate-100"
            role="img"
            aria-label={`${z.name}: ${z.wert} ${wertLabel}`}
          >
            <div
              className="h-full rounded-sm bg-brand-600"
              style={{ width: `${Math.max((z.wert / max) * 100, 1.5)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Die Lesetiefe einer Seite als Treppe.
 *
 * Vier Stufen statt eines Mittelwerts: 62 % im Mittel entsteht sowohl aus „alle
 * lesen bis zur Mitte" als auch aus „die Hälfte springt sofort ab, die andere
 * liest zu Ende". Erst die Treppe zeigt, wo es abreisst.
 *
 * Die Stufen sind geordnet, nicht verschieden — deshalb eine Farbe und keine
 * vier.
 */
export function Lesetreppe({
  stufen,
  titel,
}: {
  stufen: { marke: string; anteil: number }[];
  titel: string;
}) {
  return (
    <span className="inline-flex items-end gap-0.5" title={titel}>
      {stufen.map((s) => (
        <span key={s.marke} className="flex h-6 w-3 items-end rounded-sm bg-slate-100">
          <span
            className="w-full rounded-sm bg-brand-600"
            style={{ height: `${Math.max(s.anteil, 2)}%` }}
          />
        </span>
      ))}
    </span>
  );
}
